import { useEffect, useState, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $userUpdatePasswordStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { BaseInput, Button, ErrorMessage, Modal, Text } from '@shared/widgets';

interface IForm {
  newPassword: string;
  repeatPassword: string;
}
const DEFAULT_VALUES: IForm = { newPassword: '', repeatPassword: '' };

enum StepEnum {
  Change = 'change',
  Confirm = 'confirm',
}

interface IChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
}

export const ChangePasswordModal = (props: IChangePasswordModalProps) => {
  const userUpdatePasswordStore = useUnit($userUpdatePasswordStore.store);

  const [step, setStep] = useState<StepEnum>(StepEnum.Change);

  const { control, formState, handleSubmit, watch, getValues, setError, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const newPassword = watch('newPassword');
  const repeatPassword = watch('repeatPassword');

  const { t } = useTranslation('profile');

  useEffect(() => {
    if (newPassword === repeatPassword) setError('repeatPassword', { message: '' });
  }, [newPassword]);

  const closeHandler = () => {
    setStep(StepEnum.Change);
    reset(DEFAULT_VALUES);
    props.onClose();
  };
  const submitHandler = () => setStep(StepEnum.Confirm);
  const confirmHandler = async () => {
    const data = getValues();
    await $userUpdatePasswordStore.events.update({
      password: data.newPassword,
      confirmPassword: data.repeatPassword,
      userId: props.profileId,
    });
    closeHandler();
  };

  const passwordRequirements = useMemo(
    () => [
      { label: t('securityOptions.modal.requirements.length'), validator: (password: string) => password.length >= 8 },
      { label: t('securityOptions.modal.requirements.lowercase'), validator: (password: string) => /[a-z]/.test(password) },
      { label: t('securityOptions.modal.requirements.uppercase'), validator: (password: string) => /[A-Z]/.test(password) },
      { label: t('securityOptions.modal.requirements.number'), validator: (password: string) => /\d/.test(password) },
      { label: t('securityOptions.modal.requirements.special'), validator: (password: string) => /[^a-zA-Z0-9]/.test(password) },
    ],
    [t],
  );
  const passwordRequirementsStatus = useMemo(
    () => passwordRequirements.map((requirement) => ({ ...requirement, isValid: requirement.validator(newPassword || '') })),
    [newPassword, passwordRequirements],
  );
  return (
    <Modal title={t('securityOptions.modal.title')} isOpen={props.isOpen} onClose={closeHandler} width='332px'>
      {step === StepEnum.Change && (
        <form className='flex flex-col gap-6' noValidate onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='newPassword'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              minLength: { value: 8, message: t('securityOptions.modal.minLengthError') },
              pattern: { value: ValidateUtils.passwordRegex, message: t('errors.form.password.invalidFormat', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} type='password' placeholder={t('placeholders.newPassword', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <div className='flex flex-col gap-2'>
            <Text>{t('securityOptions.modal.hint')}:</Text>
            <ul className='flex flex-col gap-1'>
              {passwordRequirementsStatus.map((requirement, idx) => (
                <li key={idx} className='flex items-center gap-2'>
                  <Text color={requirement.isValid ? 'green' : undefined}>{requirement.isValid ? '✓' : '○'}</Text>
                  <Text size='sm'>{requirement.label}</Text>
                </li>
              ))}
            </ul>
          </div>
          <Controller
            name='repeatPassword'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              validate: (value) => value === newPassword || t('securityOptions.modal.mismatch'),
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} type='password' placeholder={t('placeholders.repeatNewPassword', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Button color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
            {t('buttons.changePassword', { ns: 'common' })}
          </Button>
        </form>
      )}
      {step === StepEnum.Confirm && (
        <div className='flex flex-col gap-6'>
          <Text weight='medium'>{t('securityOptions.modal.confirmation')}</Text>
          <div className='flex gap-4'>
            <Button color='outlined' fullWidth disabled={userUpdatePasswordStore.loading} onClick={() => setStep(StepEnum.Change)}>
              {t('buttons.close', { ns: 'common' })}
            </Button>
            <Button color='primary' fullWidth disabled={userUpdatePasswordStore.loading} onClick={confirmHandler}>
              {t('buttons.changePassword', { ns: 'common' })}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
